import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { KnowledgeAggregateItem, KnowledgeTopic } from '@app/modules/knowledge/models/knowledge.interface';
import { KnowledgeService } from '@app/modules/knowledge/services/knowledge.service';
import { Property, SegmentEvent } from '@app/shared/interfaces';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';

@Component({
  templateUrl: './dialog-edit-knowledge.component.html',
  styleUrls: ['./dialog-edit-knowledge.component.css'],
})
export class DialogEditKnowledgeComponent {
  saving = false;
  isNewItem: boolean;
  topicId: FormControl;
  content: FormControl;
  isAddingNewTopic = false;
  newTopicName: FormControl;
  initialContent: string;

  constructor(
    public dialogRef: MatDialogRef<DialogEditKnowledgeComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      propertyId: Property['id'];
      item?: KnowledgeAggregateItem;
      topic?: KnowledgeTopic;
      topics: KnowledgeTopic[];
    },
    private knowledgeService: KnowledgeService,
    private posthog: PosthogService
  ) {
    this.isNewItem = !this.data.item;
    this.initialContent = this.data.item?.content || '';
    this.topicId = new FormControl(this.data.topic?.id ?? this.knowledgeService.lastTopicId ?? this.data.topics[0]?.id);
    this.content = new FormControl(this.initialContent, [Validators.required]);
    this.newTopicName = new FormControl('', [Validators.required]);
  }

  toggleNewTopic() {
    this.isAddingNewTopic = ! this.isAddingNewTopic;

    if (! this.isAddingNewTopic) {
      this.newTopicName.reset();
    }
  }

  onContentInput(event: Event) {
    const input = event.target as HTMLTextAreaElement;
    this.content.setValue(input.value);
    this.content.updateValueAndValidity();
  }

  saveChanges() {
    if (this.isAddingNewTopic && ! this.newTopicName.valid) {
      return;
    }

    this.saving = true;

    const request = this.isNewItem
      ? this.knowledgeService.newKnowledgeItem(
          this.data.propertyId,
          this.isAddingNewTopic ? null : this.topicId.value,
          this.content.value,
          this.isAddingNewTopic ? this.newTopicName.value : undefined
        )
      : this.knowledgeService.editKnowledgeItem(
          this.data.propertyId,
          this.data.item!.id,
          this.isAddingNewTopic ? null : this.topicId.value,
          this.content.value,
          this.isAddingNewTopic ? this.newTopicName.value : undefined
        );

    request.subscribe((success) => {
      if (success) {
        if (this.isAddingNewTopic) {
          this.posthog.capture(SegmentEvent.KnowledgeHubNewTopicManuallyAdded);
        }

        if (!this.isNewItem && this.content.value !== this.initialContent) {
          this.posthog.capture(SegmentEvent.KnowledgeHubItemManuallyEdited, {
            item_id: this.data.item?.id,
            topic_id: this.isAddingNewTopic ? null : this.topicId.value,
          });
        } else if (this.isNewItem) {
          this.posthog.capture(SegmentEvent.KnowledgeHubItemManuallyAdded, {
            item_id: this.data.item?.id,
            topic_id: this.isAddingNewTopic ? null : this.topicId.value,
          });
        }
        this.dialogRef.close();
      }
    });
  }
}
